import React from 'react';
import axios from 'axios';

function SupportAgent({ 
  user, 
  agentOpen, 
  setAgentOpen, 
  agentMessages, 
  setAgentMessages, 
  agentInput, 
  setAgentInput, 
  agentLoading, 
  setAgentLoading 
}) {
  const handleAgentMessage = async () => {
    if (!agentInput.trim()) return;
    
    const userMessage = agentInput.trim();
    setAgentInput('');
    
    // Add user message to chat
    setAgentMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setAgentLoading(true);
    
    try {
      const res = await axios.post(
        '/api/chat/support',
        { 
          message: userMessage,
          userEmail: user.email,
          userTier: user.plan || 'postcard'
        }
      );
      
      if (res.data.success) {
        setAgentMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
      } else {
        setAgentMessages(prev => [...prev, { 
          role: 'assistant', 
          text: 'Sorry, es gab einen Fehler. Bitte versuche es später erneut.' 
        }]);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      if (errorMsg.includes('only from Family')) {
        setAgentMessages(prev => [...prev, { 
          role: 'assistant', 
          text: 'Der Support-Agent ist nur ab dem Family-Paket (€199) verfuegbar. Bitte upgraden, um Zugriff zu erhalten.' 
        }]);
      } else {
        setAgentMessages(prev => [...prev, { 
          role: 'assistant', 
          text: 'Support-Agent ist momentan nicht verfügbar. Bitte schreib an support@time-lock.de'
        }]);
      }
    } finally {
      setAgentLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Chat Button */}
      {!agentOpen && (
        <button
          onClick={() => setAgentOpen(true)}
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
            hover: { transform: 'scale(1.1)' }
          }}
          onMouseEnter={e => e.target.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}
        >
          👁️
        </button>
      )}
      
      {/* Chat Bubble */}
      {agentOpen && (
        <div style={{
          width: '380px',
          height: '500px',
          background: '#0c1a3a',
          border: '1px solid #2d1b4e',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: '#1e293b',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #2d1b4e'
          }}>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
              👁️ TimeLock Guide
            </div>
            <button
              onClick={() => setAgentOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </div>
          
          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {agentMessages.map((msg, idx) => (
              <div key={idx} style={{
                padding: '10px 12px',
                borderRadius: '8px',
                background: msg.role === 'assistant' ? '#2d1b4e' : '#5b21b6',
                color: '#e2e8f0',
                fontSize: '12px',
                lineHeight: '1.4',
                maxWidth: '90%',
                alignSelf: msg.role === 'assistant' ? 'flex-start' : 'flex-end',
                wordWrap: 'break-word'
              }}>
                {msg.text}
              </div>
            ))}
            {agentLoading && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '8px',
                background: '#2d1b4e',
                color: '#94a3b8',
                fontSize: '12px'
              }}>
                <span style={{ animation: 'pulse 0.8s infinite' }}>●●●</span>
              </div>
            )}
          </div>
          
          {/* Input */}
          <div style={{
            borderTop: '1px solid #2d1b4e',
            padding: '8px',
            display: 'flex',
            gap: '6px'
          }}>
            <input
              type="text"
              value={agentInput}
              onChange={e => setAgentInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleAgentMessage()}
              placeholder="Deine Frage..."
              disabled={agentLoading}
              style={{
                flex: 1,
                background: '#1e293b',
                border: '1px solid #334155',
                color: '#e2e8f0',
                padding: '6px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleAgentMessage}
              disabled={agentLoading || !agentInput.trim()}
              style={{
                background: '#a855f7',
                border: 'none',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: agentLoading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                opacity: agentLoading ? 0.5 : 1
              }}
            >
              ↑
            </button>
          </div>
        </div>
      )}
      
      {/* CSS for animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default SupportAgent;
